import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Award, Star, ArrowLeft, Linkedin, ExternalLink, Medal,
  Sparkles, TrendingUp, Target, Users, Crown, Loader2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AwardsWinnersPage = () => {
  const { year } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [winners, setWinners] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWinners();
  }, [year]);

  const fetchWinners = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/awards/winners/${year}`);
      const data = await response.json();
      
      if (data.success) {
        setWinners(data);
      } else {
        toast({
          title: 'Winners Not Found',
          description: data.message || `No winners data available for ${year}`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching winners:', error);
      toast({
        title: 'Error',
        description: 'Failed to load winners',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const categoryIcons = {
    rookie: { icon: Sparkles, gradient: 'from-green-400 to-emerald-600', color: 'text-green-600' },
    emerging_leader: { icon: TrendingUp, gradient: 'from-blue-400 to-cyan-600', color: 'text-blue-600' },
    excellence: { icon: Target, gradient: 'from-purple-400 to-indigo-600', color: 'text-purple-600' },
    manager: { icon: Users, gradient: 'from-orange-400 to-red-600', color: 'text-orange-600' },
    senior_leader: { icon: Award, gradient: 'from-yellow-400 to-amber-600', color: 'text-yellow-600' }
  };

  const getRankMedal = (rank) => {
    if (rank === 1) return { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-100', label: '🥇 Winner' };
    if (rank === 2) return { icon: Medal, color: 'text-slate-400', bg: 'bg-slate-100', label: '🥈 Runner-Up' };
    if (rank === 3) return { icon: Medal, color: 'text-orange-600', bg: 'bg-orange-100', label: '🥉 Third Place' };
    return { icon: Star, color: 'text-purple-600', bg: 'bg-purple-100', label: `#${rank}` };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
        <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!winners) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50 py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Trophy className="w-20 h-20 mx-auto mb-6 text-slate-400" />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">No Winners Data Available</h1>
          <p className="text-xl text-slate-600 mb-8">Winners for {year} have not been announced yet.</p>
          <Button onClick={() => navigate('/awards')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Awards
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-16">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <Button
            onClick={() => navigate('/awards')}
            variant="outline"
            className="mb-6 bg-white/10 border-white/20 text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Awards
          </Button>

          <div className="text-center">
            <Trophy className="w-20 h-20 mx-auto mb-6 text-yellow-300" />
            <h1 className="text-6xl font-bold mb-4">
              RecruiterIQ Global TA Awards {year}
            </h1>
            <p className="text-2xl text-purple-100 mb-2">
              Celebrating Excellence in Talent Acquisition
            </p>
            <p className="text-lg text-purple-200">
              Announced on {winners.announcement_date}
            </p>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V120Z" fill="rgb(248, 250, 252)"/>
          </svg>
        </div>
      </div>

      {/* Winners by Category */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {winners.categories.map((category) => {
          const categoryIcon = categoryIcons[category.category];
          const Icon = categoryIcon?.icon || Award;

          return (
            <div key={category.category} className="mb-16">
              {/* Category Header */}
              <div className="text-center mb-12">
                <div className={`inline-flex items-center gap-3 bg-gradient-to-r ${categoryIcon?.gradient} text-white px-8 py-4 rounded-full shadow-lg mb-4`}>
                  <Icon className="w-8 h-8" />
                  <span className="text-2xl font-bold">{category.category_display}</span>
                </div>
                <p className="text-slate-600 text-lg">{category.experience_range}</p>
              </div>

              {/* Winners Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.winners.map((winner) => {
                  const rankInfo = getRankMedal(winner.rank);
                  const RankIcon = rankInfo.icon;

                  return (
                    <Card 
                      key={winner.winner_id}
                      className={`relative overflow-hidden transition-all hover:shadow-2xl ${
                        winner.rank <= 3 ? 'border-2 border-purple-300' : ''
                      }`}
                    >
                      {/* Rank Badge */}
                      <div className={`absolute top-4 right-4 ${rankInfo.bg} ${rankInfo.color} px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1 shadow-md`}>
                        <RankIcon className="w-4 h-4" />
                        {rankInfo.label}
                      </div>

                      <CardContent className="pt-8">
                        {/* Profile Picture */}
                        <div className="flex flex-col items-center mb-4">
                          <div className={`w-24 h-24 rounded-full overflow-hidden mb-4 border-4 ${
                            winner.rank === 1 ? 'border-yellow-400' :
                            winner.rank === 2 ? 'border-slate-400' :
                            winner.rank === 3 ? 'border-orange-400' : 'border-purple-300'
                          }`}>
                            {winner.winner_profile_picture ? (
                              <img 
                                src={winner.winner_profile_picture} 
                                alt={winner.winner_name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-blue-500 text-white text-3xl font-bold">
                                {winner.winner_name.charAt(0)}
                              </div>
                            )}
                          </div>

                          {/* Name & Title */}
                          <h3 className="text-xl font-bold text-slate-900 text-center mb-1">
                            {winner.winner_name}
                          </h3>
                          {winner.winner_job_title && (
                            <p className="text-sm text-slate-600 text-center mb-1">
                              {winner.winner_job_title}
                            </p>
                          )}
                          {winner.winner_company && (
                            <p className="text-sm font-semibold text-purple-600 text-center">
                              {winner.winner_company}
                            </p>
                          )}
                        </div>

                        {/* Vote Stats */}
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-4">
                          <div className="text-center mb-3">
                            <div className="text-4xl font-bold text-purple-600">
                              {winner.total_votes}
                            </div>
                            <p className="text-sm text-slate-600 font-semibold">Total Votes</p>
                          </div>
                          
                          <div className="grid grid-cols-5 gap-1 text-xs">
                            <div className="text-center">
                              <div className="font-bold text-blue-600">
                                {winner.qualified_votes.candidate || 0}
                              </div>
                              <div className="text-slate-500">👤</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-green-600">
                                {winner.qualified_votes.peer || 0}
                              </div>
                              <div className="text-slate-500">🤝</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-purple-600">
                                {winner.qualified_votes.senior_junior || 0}
                              </div>
                              <div className="text-slate-500">👔</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-orange-600">
                                {winner.qualified_votes.manager || 0}
                              </div>
                              <div className="text-slate-500">💼</div>
                            </div>
                            <div className="text-center">
                              <div className="font-bold text-pink-600">
                                {winner.qualified_votes.hr || 0}
                              </div>
                              <div className="text-slate-500">🎯</div>
                            </div>
                          </div>
                        </div>

                        {/* LinkedIn Button */}
                        {winner.winner_linkedin_url && (
                          <Button
                            onClick={() => window.open(winner.winner_linkedin_url, '_blank')}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            size="sm"
                          >
                            <Linkedin className="w-4 h-4 mr-2" />
                            Connect on LinkedIn
                            <ExternalLink className="w-3 h-3 ml-2" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Want to be recognized in {parseInt(year) + 1}?
          </h2>
          <p className="text-xl text-purple-100 mb-8">
            Nominations for the next RecruiterIQ Global TA Awards will open soon!
          </p>
          <Button
            onClick={() => navigate('/awards')}
            size="lg"
            className="bg-yellow-400 hover:bg-yellow-500 text-purple-900 font-bold text-xl px-12 py-6 rounded-full shadow-2xl"
          >
            <Trophy className="w-6 h-6 mr-2" />
            Learn More About Awards
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AwardsWinnersPage;
